/**
 * El rango de fechas del calendario (lote B2, spec 246 punto 7; maqueta
 * #b2-fechas), sin pantalla. Los días son días de BOLIVIA (`AAAA-MM-DD`): un
 * rango va del primer día a las 00:00 al último a las 23:59, en hora de Bolivia,
 * así un cambio de las 22:00 no se va al día siguiente. Bolivia está en UTC−4
 * todo el año (no tiene horario de verano).
 */

export interface RangoDeFechas {
  /** El primer día (incluido). null = sin tope de ese lado. */
  desde: string | null;
  /** El último día (incluido). null = sin tope de ese lado. */
  hasta: string | null;
}

export const CUALQUIER_FECHA: RangoDeFechas = { desde: null, hasta: null };

export type Atajo =
  | 'HOY'
  | 'AYER'
  | '7'
  | '30'
  | 'ESTE_MES'
  | 'MES_PASADO'
  | '3_MESES'
  | 'CALENDARIO'
  | 'CUALQUIERA';

/** Los atajos de la izquierda, en el orden de la maqueta. */
export const ATAJOS: Array<{ clave: Atajo; texto: string }> = [
  { clave: 'HOY', texto: 'Hoy' },
  { clave: 'AYER', texto: 'Ayer' },
  { clave: '7', texto: 'Últimos 7 días' },
  { clave: '30', texto: 'Últimos 30 días' },
  { clave: 'ESTE_MES', texto: 'Este mes' },
  { clave: 'MES_PASADO', texto: 'Mes pasado' },
  { clave: '3_MESES', texto: 'Últimos 3 meses' },
  { clave: 'CALENDARIO', texto: 'Elegir en el calendario' },
  { clave: 'CUALQUIERA', texto: 'Cualquier fecha' },
];

const DIA_DE_BOLIVIA = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/La_Paz',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** El día de hoy (o de ese instante) en Bolivia, `AAAA-MM-DD`. */
export function diaDeBolivia(d: Date = new Date()): string {
  return DIA_DE_BOLIVIA.format(d);
}

const aFecha = (dia: string) => new Date(`${dia}T12:00:00Z`);
const aDia = (d: Date) => d.toISOString().slice(0, 10);

/** Un día más o menos (puede cruzar meses y años). */
export function sumarDias(dia: string, n: number): string {
  const d = aFecha(dia);
  d.setUTCDate(d.getUTCDate() + n);
  return aDia(d);
}

const primeroDelMes = (dia: string) => `${dia.slice(0, 7)}-01`;
function mesAnterior(dia: string): string {
  const d = aFecha(primeroDelMes(dia));
  d.setUTCMonth(d.getUTCMonth() - 1);
  return aDia(d);
}

/** El rango de un atajo. `CALENDARIO` no es un rango: abre el calendario (null). */
export function rangoDelAtajo(a: Atajo, hoy: string): RangoDeFechas | null {
  switch (a) {
    case 'HOY':
      return { desde: hoy, hasta: hoy };
    case 'AYER': {
      const ayer = sumarDias(hoy, -1);
      return { desde: ayer, hasta: ayer };
    }
    case '7':
      return { desde: sumarDias(hoy, -6), hasta: hoy };
    case '30':
      return { desde: sumarDias(hoy, -29), hasta: hoy };
    case 'ESTE_MES':
      return { desde: primeroDelMes(hoy), hasta: hoy };
    case 'MES_PASADO': {
      const inicio = mesAnterior(hoy);
      return { desde: inicio, hasta: sumarDias(primeroDelMes(hoy), -1) };
    }
    case '3_MESES': {
      // Auditoría H20: el mismo día tres meses atrás, acotado al último día de
      // ese mes (del 31 de mayo, el 28 de febrero; no el 3 de marzo).
      const [anio, mes, dia] = hoy.split('-').map(Number);
      const atras = new Date(Date.UTC(anio, mes - 1 - 3, 1, 12));
      const ultimo = new Date(
        Date.UTC(atras.getUTCFullYear(), atras.getUTCMonth() + 1, 0, 12)
      ).getUTCDate();
      atras.setUTCDate(Math.min(dia, ultimo));
      return { desde: sumarDias(aDia(atras), 1), hasta: hoy };
    }
    case 'CUALQUIERA':
      return CUALQUIER_FECHA;
    default:
      return null;
  }
}

/** El atajo que corresponde a un rango, si hay uno (para marcarlo). */
export function atajoDelRango(r: RangoDeFechas, hoy: string): Atajo {
  for (const { clave } of ATAJOS) {
    const x = rangoDelAtajo(clave, hoy);
    if (x && x.desde === r.desde && x.hasta === r.hasta) return clave;
  }
  return 'CALENDARIO';
}

/** 00:00 de ese día en Bolivia. */
export const desdeInstante = (dia: string) => new Date(`${dia}T00:00:00-04:00`);
/** 23:59:59,999 de ese día en Bolivia. */
export const hastaInstante = (dia: string) => new Date(`${dia}T23:59:59.999-04:00`);

/** ¿Un instante (ISO) cae en el rango? Sin instante (un dato viejo), sólo con «cualquier fecha». */
export function enElRango(iso: string | null | undefined, r: RangoDeFechas): boolean {
  if (r.desde == null && r.hasta == null) return true;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (r.desde != null && t < desdeInstante(r.desde).getTime()) return false;
  if (r.hasta != null && t > hastaInstante(r.hasta).getTime()) return false;
  return true;
}

const MES_CORTO = new Intl.DateTimeFormat('es-BO', { month: 'short', timeZone: 'UTC' });
const MES_LARGO = new Intl.DateTimeFormat('es-BO', { month: 'long', timeZone: 'UTC' });

/** «15 sept», o «15 sept 2025» con el año. */
export function diaCorto(dia: string, conAnio = false): string {
  const d = aFecha(dia);
  const base = `${d.getUTCDate()} ${MES_CORTO.format(d).replace('.', '')}`;
  return conAnio ? `${base} ${d.getUTCFullYear()}` : base;
}

/** «15 de septiembre de 2026», para el lector de pantalla. */
export function diaLargo(dia: string): string {
  const d = aFecha(dia);
  return `${d.getUTCDate()} de ${MES_LARGO.format(d)} de ${d.getUTCFullYear()}`;
}

/** «septiembre 2026». */
export function nombreDelMes(anio: number, mes0: number): string {
  return `${MES_LARGO.format(new Date(Date.UTC(anio, mes0, 15)))} ${anio}`;
}

/**
 * El botón del filtro: «cualquier fecha», «hoy», «15 sept – 2 oct 2026»,
 * «desde 15 sept», «hasta 2 oct». El año va al final del rango (y en cada
 * punta si son de años distintos).
 */
export function textoDelRango(r: RangoDeFechas, hoy: string): string {
  if (r.desde == null && r.hasta == null) return 'cualquier fecha';
  if (r.desde != null && r.desde === r.hasta) {
    if (r.desde === hoy) return 'hoy';
    if (r.desde === sumarDias(hoy, -1)) return 'ayer';
    return diaCorto(r.desde, true);
  }
  if (r.desde != null && r.hasta != null) {
    const mismoAnio = r.desde.slice(0, 4) === r.hasta.slice(0, 4);
    return `${diaCorto(r.desde, !mismoAnio)} – ${diaCorto(r.hasta, true)}`;
  }
  return r.desde != null ? `desde ${diaCorto(r.desde, true)}` : `hasta ${diaCorto(r.hasta!, true)}`;
}

/** El pie del calendario: qué cubre, en hora de Bolivia (D10: se dice acá). */
export function pieDelRango(r: RangoDeFechas): string {
  const zona = 'hora de Bolivia (UTC−4)';
  if (r.desde == null && r.hasta == null) return `Cualquier fecha · ${zona}`;
  if (r.desde != null && r.hasta != null) {
    // Auditoría H20: entre dos años, cada punta con el suyo.
    const conAnio = r.desde.slice(0, 4) !== r.hasta.slice(0, 4);
    return `Del ${diaCorto(r.desde, conAnio)}, 00:00, al ${diaCorto(r.hasta, conAnio)}, 23:59 · ${zona}`;
  }
  return r.desde != null
    ? `Desde el ${diaCorto(r.desde)}, 00:00 · ${zona}`
    : `Hasta el ${diaCorto(r.hasta!)}, 23:59 · ${zona}`;
}

/**
 * Las celdas de un mes, con la semana empezando el lunes: los días del mes
 * (`AAAA-MM-DD`) y `null` antes del primero.
 */
export function diasDelMes(anio: number, mes0: number): Array<string | null> {
  const primero = new Date(Date.UTC(anio, mes0, 1, 12));
  const vacios = (primero.getUTCDay() + 6) % 7; // lunes = 0
  const cuantos = new Date(Date.UTC(anio, mes0 + 1, 0, 12)).getUTCDate();
  return [
    ...Array.from({ length: vacios }, () => null),
    ...Array.from({ length: cuantos }, (_, i) => aDia(new Date(Date.UTC(anio, mes0, i + 1, 12)))),
  ];
}

/**
 * Tocar un día: el primero empieza un rango nuevo; el segundo lo cierra (si es
 * anterior, se dan vuelta). Con un rango ya cerrado, empieza otro.
 */
export function elegirDia(r: RangoDeFechas, dia: string): RangoDeFechas {
  if (r.desde != null && r.hasta == null)
    return dia < r.desde ? { desde: dia, hasta: r.desde } : { desde: r.desde, hasta: dia };
  return { desde: dia, hasta: null };
}

/**
 * Un solo día tocado es ese día entero, de las 00:00 a las 23:59 (auditoría
 * H20: «Aplicar» dejaba «desde ese día» sin cierre).
 */
export function cerrarRango(r: RangoDeFechas): RangoDeFechas {
  return r.desde != null && r.hasta == null ? { desde: r.desde, hasta: r.desde } : r;
}

export function estaEnElRango(r: RangoDeFechas, dia: string): boolean {
  if (r.desde == null) return false;
  if (r.hasta == null) return dia === r.desde;
  return dia >= r.desde && dia <= r.hasta;
}

/** Los instantes para un pedido al backend (ISO): `decididaDesde` / `decididaHasta`. */
export function instantesDelRango(r: RangoDeFechas): { desde?: string; hasta?: string } {
  return {
    ...(r.desde ? { desde: desdeInstante(r.desde).toISOString() } : {}),
    ...(r.hasta ? { hasta: hastaInstante(r.hasta).toISOString() } : {}),
  };
}
